// Helper function to update row data with WebSocket messages
export const updateRowData = (prevRow, side, msg) => {
  if (!msg || !prevRow) return prevRow;

  const currLTP = msg.lastTradedPrice || msg.ltp || 0;
  const oi = msg.openInterest || msg.currentOpenInterest || 0;
  const volume = msg.volume || 0;
  const bidPrice = msg.bestBidPrice || msg.bidPrice || 0;
  const askPrice = msg.bestAskPrice || msg.askPrice || 0;
  const closePrice = prevRow[`${side}_close`] || 0;

  // Calculate change
  const change = closePrice ? ((currLTP - closePrice) / closePrice) * 100 : 0;

  return {
    ...prevRow,
    [`${side}_data`]: {
      ...prevRow[`${side}_data`],
      ltp: currLTP,
      bestBidPrice: bidPrice,
      bestAskPrice: askPrice,
      // Preserve the token and other important fields
      token: prevRow[`${side}_data`]?.token || prevRow[`${side}_token`] || '',
      trading_symbol: prevRow[`${side}_data`]?.trading_symbol || '',
      display_name: prevRow[`${side}_data`]?.display_name || '',
      exchange: prevRow[`${side}_data`]?.exchange || ''
    },
    [`${side}_ltp`]: currLTP,
    [`${side}_oi`]: oi,
    [`${side}_volume`]: volume,
    [`${side}_change%`]: change.toFixed(2),
    [`${side}_bid`]: bidPrice,
    [`${side}_ask`]: askPrice
  };
};

// Function to find the nearest strike price to the current spot price
export const findNearestStrikeIndex = (rows, spotPrice) => {
  if (!rows.length || !spotPrice) return -1;
  
  let nearestIndex = 0;
  let minDiff = Math.abs(rows[0].strike_price - spotPrice);
  
  rows.forEach((row, index) => {
    const diff = Math.abs(row.strike_price - spotPrice);
    if (diff < minDiff) {
      minDiff = diff;
      nearestIndex = index;
    }
  });
  
  return nearestIndex;
};

// Function to determine if a strike is ITM, ATM, or OTM
export const getOptionStatus = (strikePrice, spotPrice, isCall) => {
  if (!spotPrice) return 'otm';
  
  const diff = Math.abs(strikePrice - spotPrice);
  const percentDiff = (diff / spotPrice) * 100;
  
  // If within 0.5% of spot price, consider it ATM
  if (percentDiff < 0.5) return 'atm';
  
  // For calls: ITM if strike < spot, OTM if strike > spot
  // For puts: ITM if strike > spot, OTM if strike < spot
  if (isCall) {
    return strikePrice < spotPrice ? 'itm' : 'otm';
  } else {
    return strikePrice > spotPrice ? 'itm' : 'otm';
  }
};

// Calculate the difference percentage for ATM strike
export const getStrikeDiffPercentage = (strikePrice, spotPrice) => {
  if (!spotPrice || !strikePrice) return 0;
  return ((strikePrice - spotPrice) / spotPrice * 100).toFixed(2);
};

// Initialize dynamic rows from raw data
export const initializeDynamicRows = (arrRows, exchange) => {
  return arrRows.map(row => {
    const callOption = row.call_option || {};
    const putOption = row.put_option || {};

    // Calculate initial changes
    const ceChange = ((callOption.ltp || 0) - (callOption.close_price || 0)) / (callOption.close_price || 1) * 100;
    const peChange = ((putOption.ltp || 0) - (putOption.close_price || 0)) / (putOption.close_price || 1) * 100;

    // Ensure tokens are properly initialized
    const ceToken = callOption.token || '';
    const peToken = putOption.token || '';

    return {
      ...row,
      ce_data: {
        trading_symbol: callOption.trading_symbol || '',
        display_name: callOption.display_name || '',
        ltp: callOption.ltp || 0,
        close: parseFloat(callOption.close_price) || 0,
        exchange: callOption.exchange || exchange,
        token: ceToken,
        lotsize: callOption.lotsize || 0,
        tick_size: callOption.tick_size || 0,
        execution: callOption.execution || '',
        bestBidPrice: callOption.bestBidPrice || 0,
        bestAskPrice: callOption.bestAskPrice || 0,
        tradable: callOption.tradable
      },
      pe_data: {
        trading_symbol: putOption.trading_symbol || '',
        display_name: putOption.display_name || '',
        ltp: putOption.ltp || 0,
        close: parseFloat(putOption.close_price) || 0,
        exchange: putOption.exchange || exchange,
        token: peToken,
        lotsize: putOption.lotsize || 0,
        tick_size: putOption.tick_size || 0,
        execution: putOption.execution || '',
        bestBidPrice: putOption.bestBidPrice || 0,
        bestAskPrice: putOption.bestAskPrice || 0,
        tradable: putOption.tradable
      },
      ce_token: ceToken,
      pe_token: peToken,
      ce_ltp: callOption.ltp || 0,
      pe_ltp: putOption.ltp || 0,
      ce_oi: callOption.openInterest || 0,
      pe_oi: putOption.openInterest || 0,
      ce_close: parseFloat(callOption.close_price) || 0,
      pe_close: parseFloat(putOption.close_price) || 0,
      ce_volume: callOption.volume || 0,
      pe_volume: putOption.volume || 0,
      ce_change: ceChange || 0,
      pe_change: peChange || 0,
      ce_bid: callOption.bestBidPrice || 0,
      pe_bid: putOption.bestBidPrice || 0,
      ce_ask: callOption.bestAskPrice || 0,
      pe_ask: putOption.bestAskPrice || 0,
      strike_price: row.strike_price || 0
    };
  });
}; 